/**
 * Shared bits for payroll, expenses and reports: scope (business, branch, currency), idempotency intent keys,
 * the four-state gate, and money entry. Money is integer paise (CLAUDE.md rule 1); nothing here sums or rounds it.
 */
import { Button, Card, EmptyState, ErrorState, Skeleton, formatMoney, type IconName } from "@helmr/ui";
import type { UseQueryResult } from "@tanstack/react-query";
import { useCallback, useId, useRef, useState, type ReactNode } from "react";
import { isApiError, newIdempotencyKey } from "../../api/client";
import { NoAccess } from "../../app/NoAccess";
import { useAppContext, useSessionSnapshot, type Context } from "../../app/permissions";
import { useServices } from "../../app/services";
import { today as todayOf } from "../../lib/dates";
import s from "./payroll.module.css";

export { s as css };

/** Who is asking and for which branch. Every query key carries `scopeKey`, so a cached list never crosses a business or branch. */
export function useScope() {
  const ctx = useAppContext();
  const snap = useSessionSnapshot();
  const { now } = useServices();
  const currency = ctx.me?.business.currency ?? "INR";
  const locale = ctx.locale;
  /** `undefined` when "All branches" is chosen: the list call then omits the filter. */
  const branchId = snap.branchFilter === "all" ? undefined : snap.branchFilter;
  /** Month actions need exactly one branch: the filter, else the session's. */
  const monthBranchId = branchId ?? snap.branchId ?? undefined;
  return {
    ctx, locale, currency, digits: fractionDigits(locale, currency), branchId, monthBranchId, staffId: snap.staffId,
    scopeKey: [snap.businessId, snap.branchId, snap.branchFilter] as const,
    today: todayOf(now(), ctx.timeZone),
    money: (paise: number) => formatMoney(paise, currency, locale),
    moneyIn: (paise: number, cur: string, loc: string) => formatMoney(paise, cur, loc),
  };
}
export type Scope = ReturnType<typeof useScope>;

/** One key per user intent: made when a form opens, reused by every retry of that submit, replaced after success. */
export function useIntentKey() {
  const ref = useRef<string>(newIdempotencyKey());
  return { key: () => ref.current, renew: useCallback(() => { ref.current = newIdempotencyKey(); }, []) };
}

export const errorText = (e: unknown) => (isApiError(e) ? e.message : "Something went wrong. Try again.");
export const requestIdOf = (e: unknown) => (isApiError(e) ? e.requestId : null);

export function TableSkeleton({ rows = 5, label }: { rows?: number; label: string }) {
  return (
    <div className={s.sk} aria-busy="true" aria-label={label} role="status">
      {Array.from({ length: rows }, (_, i) => <Skeleton key={i} height={22} />)}
    </div>
  );
}

export function Failure({ error, onRetry, what }: { error: unknown; onRetry: () => void; what: string }) {
  if (isApiError(error) && error.status === 403) return <NoAccess compact />;
  // Only the worker's own "No route for …" 404 means the API is missing; any other 404 is a record that is not there.
  if (isApiError(error) && error.status === 404 && /^No route for /.test(error.message)) return <EmptyState compact icon="clock" title="Not connected yet" description={`${what} will show here once it is connected.`} />;
  if (isApiError(error) && error.status === 404) return <EmptyState compact icon="alert" title={error.message} description={`Could not find ${what}.`} />;
  return <ErrorState compact title={`Could not load ${what}`} message={errorText(error)} requestId={requestIdOf(error)} onRetry={onRetry} />;
}

/** Skeleton → error (with Retry and request id) → data. Empty states are the caller's, since the words differ. */
export function Gate<T>({ query, what, skeleton, children }: { query: Pick<UseQueryResult<T>, "data" | "error" | "isPending" | "refetch">; what: string; skeleton?: ReactNode; children: (d: T) => ReactNode }) {
  if (query.isPending) return <>{skeleton ?? <TableSkeleton label={`Loading ${what}`} />}</>;
  if (query.error) return <Failure error={query.error} what={what} onRetry={() => void query.refetch()} />;
  return <>{children(query.data as T)}</>;
}

export function Empty({ icon, title, description, action }: { icon: IconName; title: string; description?: string; action?: ReactNode }) {
  return <EmptyState compact icon={icon} title={title} description={description} action={action} />;
}

export function PageHead({ title, sub, children }: { title: string; sub?: ReactNode; children?: ReactNode }) {
  return (
    <div className={s.head}>
      <div><h1>{title}</h1>{sub ? <p>{sub}</p> : null}</div>
      {children ? <div className={s.actions}>{children}</div> : null}
    </div>
  );
}

export function Field({ label, error, hint, children }: { label: string; error?: string | null; hint?: ReactNode; children: (p: { id: string; "aria-invalid": boolean | undefined; "aria-describedby": string | undefined }) => ReactNode }) {
  const id = useId();
  const sub = error ?? hint;
  return (
    <div className={s.field}>
      <label htmlFor={id}><span>{label}</span></label>
      {children({ id, "aria-invalid": error ? true : undefined, "aria-describedby": sub ? `${id}-h` : undefined })}
      {sub ? <small id={`${id}-h`} className={error ? s.err : undefined}>{sub}</small> : null}
    </div>
  );
}

export const CardBox = Card;

/** A role without the page's lever(s) gets the no-access state before any call is made (the server refuses anyway). */
export function RequireView({ allow, children }: { allow: (has: Context["has"]) => boolean; children: ReactNode }) {
  const ctx = useAppContext();
  if (ctx.status === "ready" && !allow(ctx.has)) return <NoAccess />;
  return <>{children}</>;
}

/* ---------------------------------------------------------------- money entry */

export const fractionDigits = (locale: string, currency: string) => new Intl.NumberFormat(locale, { style: "currency", currency }).resolvedOptions().maximumFractionDigits ?? 2;
const decimalMark = (locale: string) => new Intl.NumberFormat(locale).formatToParts(1.5).find((p) => p.type === "decimal")?.value ?? ".";

/**
 * "1,234.50" → 123450 minor units. String arithmetic only, never a float. Returns null for anything that is not a plain
 * amount (letters, a second dot, more decimals than the currency has). `allowNegative` is for signed adjustments.
 * Where the locale's decimal mark is a comma, the last `.` or `,` is the decimal point if the currency's digits can
 * follow it ("1.500,50", and the "1500.5" that `minorToInput` prefills); otherwise it groups ("1.500").
 */
export function parseMoneyInput(text: string, currency: string, locale: string, allowNegative = false): number | null {
  const digits = fractionDigits(locale, currency);
  let t = text.trim().replace(/[\s_]/g, "");
  if (decimalMark(locale) === ",") {
    const at = Math.max(t.lastIndexOf(","), t.lastIndexOf("."));
    const isPoint = at >= 0 && t.length - 1 - at <= digits;
    t = isPoint ? `${t.slice(0, at).replace(/[.,]/g, "")}.${t.slice(at + 1)}` : t.replace(/[.,]/g, "");
  } else {
    t = t.replace(/,/g, "");
  }
  let sign = 1;
  if (allowNegative && t.startsWith("-")) { sign = -1; t = t.slice(1); }
  const m = /^(\d+)(?:\.(\d*))?$/.exec(t);
  if (!m) return null;
  const frac = m[2] ?? "";
  if (frac.length > digits) return null;
  const minor = Number(`${m[1]}${frac.padEnd(digits, "0")}`);
  if (!Number.isSafeInteger(minor)) return null;
  return sign * minor || 0;
}

export function MoneyInput({ id, value, onChange, ...rest }: { id: string; value: string; onChange: (v: string) => void; "aria-invalid"?: boolean; "aria-describedby"?: string; placeholder?: string }) {
  return <input id={id} className={s.input} inputMode="decimal" autoComplete="off" value={value} onChange={(e) => onChange(e.target.value)} {...rest} />;
}

export function useBusy() {
  const [busy, setBusy] = useState(false);
  return { busy, run: async <T,>(fn: () => Promise<T>): Promise<T | undefined> => { setBusy(true); try { return await fn(); } finally { setBusy(false); } } };
}

export { Button };

/** `YYYY-MM` helpers on strings only (rule 3): no Date arithmetic across zones. */
export const monthOf = (ymd: string) => ymd.slice(0, 7);
export function shiftMonth(month: string, delta: number): string {
  const [y, m] = month.split("-").map(Number) as [number, number];
  const idx = y * 12 + (m - 1) + delta;
  return `${Math.floor(idx / 12)}-${String((idx % 12) + 1).padStart(2, "0")}`;
}
export function monthLabel(month: string, locale: string): string {
  const [y, m] = month.split("-").map(Number) as [number, number];
  return new Intl.DateTimeFormat(locale, { month: "long", year: "numeric", timeZone: "UTC" }).format(new Date(Date.UTC(y, m - 1, 1)));
}
export const isMonth = (v: string | undefined): v is string => !!v && /^\d{4}-(0[1-9]|1[0-2])$/.test(v);
export const isDate = (v: string) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v);
  if (!m) return false;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const at = new Date(Date.UTC(y, mo - 1, d)); // calendar validity only: round-trips, no zone involved
  return at.getUTCFullYear() === y && at.getUTCMonth() === mo - 1 && at.getUTCDate() === d;
};
